import { computed, type ComputedRef } from "vue";
import { storeToRefs } from "pinia";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";

import type { TooltipBindingValue } from "@/directives/tooltipDirective";
import type { TooltipPlacement } from "@/stores/useTooltip";
import { useProject } from "@/stores/project/project.store";
import { VIEWER_READ_ONLY_HINT, VIEWER_READ_ONLY_MESSAGE } from "@/providers/Permissions/projectRoleAccess";

import { useProjectRevisionsStore } from "../stores/projectRevisions.store";
import {
  REVISION_READ_ONLY_HINT,
  REVISION_READ_ONLY_MESSAGE,
  blockedByPreviewFor,
  type RevisionReadOnly,
} from "./useRevisionReadOnly.composable";

/**
 * `useRevisionReadOnly` plus the project role: read-only when the user may not write to THIS
 * project at all, as well as when they are previewing a frozen revision of it (RTLCA-5542).
 *
 * Two different reasons, one surface. A Viewer's waste write is refused by Hasura's row
 * permissions, which is a rolled-back document and an unhandled rejection rather than an answer —
 * so the control has to be inert before it is pressed. A preview is refused by us, because writes
 * cannot be pinned to a frozen revision and would land on the current one. Both end in "you cannot
 * write here", which is why they share the `RevisionReadOnly` shape and every call site keeps its
 * destructuring; only the wording differs, and the role case must never tell a Viewer to switch
 * revision — switching would not give them the permission.
 *
 * Deliberately a SIBLING of `useRevisionReadOnly` rather than a widening of it: that composable is
 * consumed by transport, materials, Speckle and the dashboard, several of which gate the role
 * themselves further up. Folding the role in there would silently change those pages.
 *
 * Fails closed while `permissions` is still null (the project's permissions have not loaded yet):
 * a guard that guesses "allowed" during a load is a guard that lets the first click through.
 */

/**
 * The refusal wording now lives with the per-project role predicates
 * (`@/providers/Permissions/projectRoleAccess`), so the projects overview can say the same thing
 * without importing out of this page's folder (RTLCA-5583). Re-exported here because this module is
 * where every existing caller — and this composable's own tests — import it from.
 */
export { VIEWER_READ_ONLY_HINT, VIEWER_READ_ONLY_MESSAGE };

const VIEWER_TOAST_ID = "viewer-read-only";
const REVISION_TOAST_ID = "revision-read-only";

/**
 * `blockedByPreviewFor` widened to the role: the provider/store form of `blockedByPreview`, for a
 * guard that lives outside a route-bound component — a composable handed its projectId per call
 * (`useSpeckleEpdAssign`, shared by the 3D viewer's three tool panels). Same conditions, same
 * refusals, resolved at call time so a captured id or a stale flag can never answer for the wrong
 * project.
 *
 * The role comes from the project store, which holds the permissions of the project currently open
 * — the same project every caller passes here, since they all live under its route. Fails closed
 * while those permissions are still null, for the same reason the composable does: a guard that
 * guesses "allowed" during a load is a guard that lets the first click through.
 */
export const blockedFromProjectEditFor = (projectId: string): boolean => {
  const { permissions } = storeToRefs(useProject());
  if (!(permissions.value?.includes("project.edit") ?? false)) {
    useToast().info(VIEWER_READ_ONLY_MESSAGE, { id: VIEWER_TOAST_ID });
    return true;
  }
  return blockedByPreviewFor(projectId);
};

export interface ProjectEditReadOnly extends RevisionReadOnly {
  /**
   * The ROLE half of `isReadOnly`, on its own: true when this user may not write to the project at
   * all, whether or not they are also previewing a revision.
   *
   * Exists because the two reasons do not always deserve the same treatment. Transport deliberately
   * leaves its add-mapping fields live while a frozen revision is previewed (RTLCA-5254) — an editor
   * needs to type a distance to see what the mapping would come to, and only the Add is shut — but a
   * Viewer must find the fields themselves inert (RTLCA-5581). A surface that treats both reasons
   * alike should keep using `isReadOnly`; only reach for this when a control is blocked by the role
   * and NOT by the preview.
   */
  lacksEditPermission: ComputedRef<boolean>;
}

/**
 * @param placement where the explanatory tooltip should sit relative to the blocked control.
 */
export const useProjectEditReadOnly = (placement: TooltipPlacement = "bottom"): ProjectEditReadOnly => {
  const route = useRoute();
  const revisionsStore = useProjectRevisionsStore();
  const { permissions } = storeToRefs(useProject());

  // Same resolution as `useRevisionReadOnly`: every caller lives under `/project/:id`, and the id is
  // read reactively rather than captured so switching project without unmounting still asks about
  // the right one. The project store is keyed by the project it currently holds, which is the same.
  const projectId = computed(() => String(route.params.id ?? ""));

  // The predicate the rest of the app uses for "may edit this project" (MaterialMapping.vue,
  // ProjectNavigation.vue).
  const lacksEditPermission = computed(() => !(permissions.value?.includes("project.edit") ?? false));

  const isPreviewingRevision = computed(() => revisionsStore.isPreviewing(projectId.value));

  const isReadOnly = computed(() => lacksEditPermission.value || isPreviewingRevision.value);

  // The role is the more fundamental refusal, so it wins the wording when both apply: telling a
  // Viewer to go back to the current revision would send them somewhere they still cannot write.
  const readOnlyHint = computed(() => (lacksEditPermission.value ? VIEWER_READ_ONLY_HINT : REVISION_READ_ONLY_HINT));

  const readOnlyTooltip = computed<TooltipBindingValue>(() => ({
    text: readOnlyHint.value,
    variant: "label",
    placement,
    // Suppressed rather than unbound, so the same binding can stay on a control that is only
    // sometimes blocked without ever explaining a control the user can actually use.
    disabled: !isReadOnly.value,
  }));

  /**
   * Keeps the `blockedByPreview` name the `RevisionReadOnly` interface defines — every waste
   * handler already calls it first and returns — but answers for both blocks, and speaks the
   * refusal that matches the one in force.
   */
  const blockedByPreview = (): boolean => {
    if (!isReadOnly.value) return false;
    useToast().info(lacksEditPermission.value ? VIEWER_READ_ONLY_MESSAGE : REVISION_READ_ONLY_MESSAGE, {
      id: lacksEditPermission.value ? VIEWER_TOAST_ID : REVISION_TOAST_ID,
    });
    return true;
  };

  const runUnlessBlocked = (action: () => unknown): void => {
    if (blockedByPreview()) return;
    action();
  };

  const stopBlockedClick = (event: MouseEvent): void => {
    if (!isReadOnly.value) return;
    // Stopped only while blocked — when writing is allowed the click must keep bubbling to whatever
    // the surrounding row does with it (expand, open a modal).
    event.stopPropagation();
    // A programmatic click (Switch's mount sync) is not a user attempt, so it must not toast.
    if (!event.isTrusted) return;
    blockedByPreview();
  };

  return { isReadOnly, lacksEditPermission, readOnlyTooltip, blockedByPreview, runUnlessBlocked, stopBlockedClick };
};

export default useProjectEditReadOnly;
